import React, {useEffect, useRef, useState} from "react";
import {Box, Paper, Typography} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import CheckboxInputField from "@/lib/components/inputfields/checkbox-input-field";
import {ChangeHandler} from "@/lib/modelstate";
import {FieldError} from "@/lib/utils";
import {CardEx} from "@/lib/components/CardEx";
import PageLoadingSpinner from "@/lib/components/PageLoadingSpinner";
import {useTranslation} from "react-i18next";
import {AuthI18N, CommonI18N} from "@/translations";
import {LANGUAGE_TO_LNG} from "@/app/locale/languageCodes";
import {LegalDocumentContent, LegalDocumentLoadState} from "@/common/legal/legalDocuments";
import {LEGAL_DOCUMENT_SCOPE_CLASS, prepareLegalDocumentHtml} from "@/common/legal/legalDocumentHtml";
import {canAcceptAgreement, ScrollMetrics} from "@/common/legal/agreementAcceptance";

/** Load state of the agreement text: request in flight, content rendered, or load failed. */
export type AgreementLoadState = LegalDocumentLoadState;

interface AgreementProps {
    name: string;
    title?: string;
    agreement?: LegalDocumentContent;
    loadState: AgreementLoadState;
    value?: boolean;
    onChange: ChangeHandler;
    error?: FieldError;
}

/** Reads the scroll geometry of the panel; `undefined` before the panel is mounted. */
const readScrollMetrics = (element: HTMLElement | null): ScrollMetrics | undefined =>
        element
                ? {scrollHeight: element.scrollHeight, clientHeight: element.clientHeight, scrollTop: element.scrollTop}
                : undefined;

const Agreement: React.FC<AgreementProps> = ({name, title, agreement, loadState, value, error, onChange}) => {
    const { t } = useTranslation();
    const [canAccept, setCanAccept] = useState(false);
    const textBoxRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    // The panel holds successfully loaded content — a precondition for enabling acceptance, because the
    // scroll geometry of an empty panel is indistinguishable from that of a fully read short document.
    const isContentLoaded = loadState === "loaded" && agreement !== undefined;

    // Mirrors `value` for the content effect below, which must know whether consent was already given
    // without taking `value` as a dependency — re-running it on every checkbox toggle would rebuild the
    // document's DOM and reset the reader's scroll position.
    const isAcceptedRef = useRef(!!value);

    // When the value is already true the checkbox is checked, so acceptance is enabled right away.
    // Declared before the content effect so the ref already holds the current value when that one runs.
    useEffect(() => {
        isAcceptedRef.current = !!value;
        if (value) {
            setCanAccept(true);
        }
    }, [value]);

    // The agreement text is inserted as a prepared fragment (meta-refresh removal, style scoping) via
    // `replaceChildren` instead of through `innerHTML` — the same render path the public info pages use.
    // Going through `innerHTML` again would let a `<style>` payload break out of its element on the second
    // parse (see `prepareLegalDocumentHtml`), and an unscoped `<style>` block of the terms and conditions
    // would restyle the surrounding application chrome.
    useEffect(() => {
        const container = contentRef.current;
        if (!container) {
            return;
        }
        try {
            if (agreement) {
                container.replaceChildren(prepareLegalDocumentHtml(agreement.content));
            } else {
                container.replaceChildren();
            }
            // Measured only AFTER the content is in the DOM — before that the panel still reports the
            // geometry of the previous (or empty) content. Re-evaluated from scratch on every content
            // change, which also revokes a stale release when a new, longer text replaces a read one.
            const metrics = readScrollMetrics(textBoxRef.current);
            setCanAccept(
                    metrics
                            ? canAcceptAgreement(metrics, isContentLoaded, isAcceptedRef.current)
                            : isAcceptedRef.current
            );
        } catch (error: unknown) {
            // The prepared fragment is built imperatively; an unexpected failure here would otherwise
            // travel up through the error boundary and tear down the whole registration wizard.
            console.error("Failed to render the agreement text", error);
            container.replaceChildren();
            setCanAccept(isAcceptedRef.current);
        }
    }, [agreement, isContentLoaded]);

    const handleScroll = () => {
        const metrics = readScrollMetrics(textBoxRef.current);
        if (!metrics) {
            return;
        }
        // Scroll events also fire while the panel is emptied (the browser clamps `scrollTop` to 0), so
        // the load state is part of the decision — see `canAcceptAgreement`.
        if (canAcceptAgreement(metrics, isContentLoaded, isAcceptedRef.current)) {
            setCanAccept(true);
        }
    };

    return (
        <CardEx title={title}>
            <Paper
                elevation={2}
                sx={{
                    p: 2,
                    mb: 2,
                    maxHeight: "600px",
                    overflowY: "auto",
                    borderRadius: "8px",
                    backgroundColor: theme.palette.mode === "light" ? theme.palette.grey[200] : theme.palette.grey[800]
                }}
                ref={textBoxRef}
                onScroll={handleScroll}
            >
                {loadState === "loading" && (
                        <Box sx={{position: "relative", minHeight: "80px"}}>
                            <PageLoadingSpinner fullScreen={false}/>
                        </Box>
                )}
                {loadState === "error" && <Typography>{t(CommonI18N.message.documentLoadFailed)}</Typography>}
                {/* Always mounted: the content is written into this element imperatively, so the ref must
                not change between the request and the response. */}
                <Typography
                    component="div"
                    ref={contentRef}
                    className={LEGAL_DOCUMENT_SCOPE_CLASS}
                    lang={agreement ? LANGUAGE_TO_LNG[agreement.effectiveLanguage] : undefined}
                    sx={{ lineHeight: 1.6, fontSize: "0.95rem" }}
                />
            </Paper>
            <Box sx={{ mt: 2 }}>
                <CheckboxInputField
                    name={name}
                    label={t(AuthI18N.login.field.termsAgreement)}
                    value={value}
                    onChange={onChange}
                    disabled={!canAccept}
                    error={error}
                />
                {/* Only meaningful while readable content is on screen — during loading or after a
                failure there is nothing to scroll to, and the hint would point at a dead end. */}
                {!canAccept && isContentLoaded && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 1, fontStyle: "italic", textAlign: "center" }}
                    >
                        {t(AuthI18N.login.message.scrollDown)}
                    </Typography>
                )}
            </Box>
        </CardEx>
    );
};

export default Agreement;
